import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { inputCls, btnPrimary, btnGhost, labelCls } from "../lib/ui";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "nn_admin_token";

const blankPost = () => ({
  id: null, title: "", slug: "", metaDescription: "", body: "",
  published: false, publishDate: new Date().toISOString().slice(0, 10),
});

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [ownerCode, setOwnerCode] = useState("");
  const [testPdf, setTestPdf] = useState("");

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/posts`, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TKEY);
        setToken("");
        toast.error("Session expired. Please log in again.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) loadPosts();
  }, [token, loadPosts]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TKEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.status === 429 ? "Too many attempts. Please wait." : "Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TKEY);
    setToken("");
    setPosts([]);
  };

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/posts/${editing.id}`, editing, authHeader);
        toast.success("Post updated.");
      } else {
        await axios.post(`${API}/admin/posts`, editing, authHeader);
        toast.success("Post created.");
      }
      setEditing(null);
      loadPosts();
    } catch (e) {
      toast.error("Could not save the post.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, authHeader);
      toast.success("Post deleted.");
      loadPosts();
    } catch (e) {
      toast.error("Could not delete the post.");
    }
  };

  const runOwnerTest = async () => {
    if (!ownerCode.trim()) {
      toast.error("Enter the owner test code.");
      return;
    }
    setTestPdf("");
    try {
      const sample = {
        firstName: "Jordan", lastName: "Sample", state: "GA",
        companyName: "Sample Company LLC", companyAddress: "123 Sample Street, Atlanta, GA 30303",
        employerNumber: "SAMPLE-001", payDate: new Date().toISOString().slice(0, 10),
        paymentMethod: "direct_deposit", employeeAddress: "123 Sample Street, Atlanta, GA 30303",
        earnings: [{ description: "Regular", payRate: "25.70", hours: "40" }], payPeriodsYTD: 40,
      };
      const { data } = await axios.post(`${API}/paystubs`, { form_data: sample });
      await axios.post(`${API}/paystubs/${data.id}/owner-unlock`, { code: ownerCode.trim() });
      setTestPdf(`${API}/paystubs/${data.id}/pdf`);
      toast.success("Owner test unlocked — PDF ready.");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Invalid code." : e?.response?.status === 429 ? "Too many attempts." : "Test failed.");
    }
  };

  if (!token) {
    return (
      <>
        <Seo title="Admin | Nexus Numbers" description="Admin login." robots="noindex, nofollow" />
        <main className="max-w-md mx-auto px-5 py-20 min-h-[60vh]">
          <h1 className="font-display text-2xl font-bold text-[#0F172A] mb-6">Admin sign in</h1>
          <form onSubmit={login} className="space-y-4 rounded-xl border border-[#D0E2FF] bg-white p-6">
            <div>
              <Label className={labelCls}>Password</Label>
              <Input data-testid="admin-password" type="password" className={inputCls} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoFocus />
            </div>
            <Button type="submit" data-testid="admin-login-button" disabled={loading} className={`${btnPrimary} w-full rounded-full py-6`}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </main>
      </>
    );
  }

  return (
    <>
      <Seo title="Admin — Posts | Nexus Numbers" description="Manage blog posts." robots="noindex, nofollow" />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 min-h-[60vh]">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-2xl font-bold text-[#0F172A]">Blog admin</h1>
          <div className="flex gap-3">
            <Button data-testid="new-post-button" onClick={() => setEditing(blankPost())} className={`${btnPrimary} rounded-full`}>
              <Plus className="w-4 h-4 mr-1" /> New post
            </Button>
            <Button variant="outline" onClick={logout} className={btnGhost}>
              <LogOut className="w-4 h-4 mr-1" /> Log out
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-amber-300 bg-amber-50 p-6 mb-8" data-testid="owner-test-panel">
          <div className="font-display font-semibold text-amber-900 mb-1">Owner test checkout (skip payment)</div>
          <p className="text-sm text-amber-800 mb-3">
            Private tool. Generates a sample paid statement using the owner test code so you can verify the full flow without paying.
          </p>
          <div className="flex gap-2 max-w-md">
            <Input data-testid="owner-code-input" type="password" className={inputCls} value={ownerCode}
              onChange={(e) => setOwnerCode(e.target.value)} placeholder="Owner test code" />
            <Button data-testid="owner-test-run" onClick={runOwnerTest} className={`${btnPrimary} shrink-0`}>
              Generate test PDF
            </Button>
          </div>
          {testPdf && (
            <a href={testPdf} target="_blank" rel="noreferrer" data-testid="owner-test-pdf-link"
              className="inline-block mt-3 text-sm font-medium text-[#1E40AF] hover:underline">
              Open generated test PDF
            </a>
          )}
        </div>

        {editing && (
          <div className="rounded-xl border border-[#D0E2FF] bg-white p-6 mb-8 space-y-4">            <div className="font-display font-semibold text-[#0F172A]">{editing.id ? "Edit post" : "New post"}</div>
            <div>
              <Label className={labelCls}>Title</Label>
              <Input data-testid="post-title" className={inputCls} value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Slug (optional)</Label>
                <Input data-testid="post-slug" className={inputCls} value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto from title" />
              </div>
              <div>
                <Label className={labelCls}>Publish date</Label>
                <Input data-testid="post-date" type="date" className={inputCls} value={editing.publishDate}
                  onChange={(e) => setEditing({ ...editing, publishDate: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className={labelCls}>Meta description</Label>
              <Input data-testid="post-meta" className={inputCls} value={editing.metaDescription}
                onChange={(e) => setEditing({ ...editing, metaDescription: e.target.value })}
                placeholder="Under 155 characters" />
            </div>
            <div>
              <Label className={labelCls}>Body (use # / ## / ### for headings)</Label>
              <Textarea data-testid="post-body" className={`${inputCls} min-h-[220px] font-mono text-sm`} value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox data-testid="post-published" checked={editing.published}
                onCheckedChange={(v) => setEditing({ ...editing, published: v === true })} />
              <span className="text-sm text-[#475569]">Published</span>
            </label>
            <div className="flex gap-3">
              <Button data-testid="save-post-button" onClick={save} className={`${btnPrimary} rounded-full px-6`}>Save</Button>
              <Button variant="outline" onClick={() => setEditing(null)} className={btnGhost}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-[#D0E2FF] bg-white overflow-hidden">
          {posts.length === 0 ? (
            <div className="p-6 text-[#64748B] text-sm">No posts yet.</div>
          ) : (
            posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.slug}`}
                className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[#EFF4FF] last:border-0">
                <div className="min-w-0">
                  <div className="font-medium text-[#0F172A] truncate">{p.title}</div>
                  <div className="text-xs text-[#94A3B8]">/{p.slug} · {p.publishDate} · {p.published ? "Published" : "Draft"}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`edit-${p.slug}`} onClick={() => setEditing(p)} className="text-[#1E40AF]">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-${p.slug}`} onClick={() => del(p.id)} className="text-[#EF4444]">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </>
  );
};
